import React, { useState, useMemo } from "react";
import { format, isValid, parse, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * The single date range picker for desktop (DESIGN.md §5.2).
 *
 * - Closed state: chip format: "1 Sep" or "1 Sep – 7 Sep" ("1 Sep 2025" when year differs from today).
 * - Presets first: Today, Yesterday, Last 7 days, Last 30 days, This month, Last month, Custom.
 * - Business day aware: optional businessDayStart renders footer ("Day starts 6:00 am").
 * - Range selection via Calendar mode="range".
 * - Bounded by optional min and max dates.
 */

const ISO = "yyyy-MM-dd";
const TYPED_FORMATS = ["dd-MM-yyyy", "d-M-yyyy", "dd/MM/yyyy", "d/M/yyyy", ISO];

const parseDay = text => {
  if (!text) return null;
  if (text instanceof Date) return isValid(text) ? text : null;
  const raw = String(text).trim();
  if (!raw) return null;
  for (const pattern of TYPED_FORMATS) {
    const day = parse(raw, pattern, new Date());
    if (isValid(day) && day.getFullYear() > 1900) return day;
  }
  return null;
};

const formatChipDate = day => {
  if (!day || !isValid(day)) return "";
  const thisYear = new Date().getFullYear();
  return format(day, day.getFullYear() === thisYear ? "d MMM" : "d MMM yyyy");
};

export const chipRangeLabel = (fromDay, toDay, placeholder = "Select date range") => {
  if (!fromDay) return placeholder;
  const fromFormatted = formatChipDate(fromDay);
  if (!toDay || format(fromDay, ISO) === format(toDay, ISO)) {
    return fromFormatted;
  }
  const toFormatted = formatChipDate(toDay);
  return `${fromFormatted} – ${toFormatted}`;
};

export const DEFAULT_PRESETS = [
  { id: "today", label: "Today", getRange: () => { const now = new Date(); return { from: now, to: now }; } },
  { id: "yesterday", label: "Yesterday", getRange: () => { const y = subDays(new Date(), 1); return { from: y, to: y }; } },
  { id: "7d", label: "Last 7 days", getRange: () => ({ from: subDays(new Date(), 6), to: new Date() }) },
  { id: "30d", label: "Last 30 days", getRange: () => ({ from: subDays(new Date(), 29), to: new Date() }) },
  { id: "this_month", label: "This month", getRange: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) },
  { id: "last_month", label: "Last month", getRange: () => { const prev = subMonths(new Date(), 1); return { from: startOfMonth(prev), to: endOfMonth(prev) }; } },
  { id: "custom", label: "Custom", getRange: null },
];

export function DateRangePicker({
  value, // { from, to } as ISO strings or Dates
  onChange, // ({ from: "YYYY-MM-DD", to: "YYYY-MM-DD", preset: string }) => void
  preset, // currently active preset id, if any
  presets = DEFAULT_PRESETS,
  businessDayStart, // e.g. "06:00"
  min,
  max,
  placeholder = "Select range",
  ariaLabel = "Date range",
  testId = "date-range-picker",
  className,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  // The first day clicked since the popover opened. react-day-picker's range mode
  // EXTENDS the range already selected: with 1-7 Oct showing, a click on 20 Oct
  // gave 1-20 Oct and closed, so a new start could never be picked (owner,
  // 2026-10-26). Here the first click starts a new range, the second ends it.
  const [anchor, setAnchor] = useState(null);
  const openChange = next => {
    setAnchor(null);
    setOpen(next);
  };

  const fromDay = parseDay(value?.from);
  const toDay = parseDay(value?.to);

  const minDay = parseDay(min);
  const maxDay = parseDay(max);

  const selectedRange = useMemo(() => ({
    from: fromDay || undefined,
    to: toDay || undefined,
  }), [fromDay, toDay]);

  const handleDayClick = day => {
    if (!day) return;
    if (!anchor) {
      setAnchor(day);
      return;
    }
    const [start, end] = day < anchor ? [day, anchor] : [anchor, day];
    onChange?.({ from: format(start, ISO), to: format(end, ISO), preset: "custom" });
    setAnchor(null);
    setOpen(false);
  };

  const handleApplyPreset = p => {
    if (p.id === "custom") {
      onChange?.({ from: value?.from || "", to: value?.to || "", preset: "custom" });
      return;
    }
    const r = p.getRange();
    if (r) {
      const fromStr = format(r.from, ISO);
      const toStr = format(r.to, ISO);
      onChange?.({ from: fromStr, to: toStr, preset: p.id });
      setOpen(false);
    }
  };

  const formatTimeHelper = timeStr => {
    if (!timeStr) return null;
    const [h, m = "00"] = timeStr.split(":");
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "pm" : "am";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  return (
    <div className={cn("inline-flex items-center", className)} data-testid={testId}>
      <Popover open={open} onOpenChange={openChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-8 inline-flex items-center gap-2 rounded-md border bg-background px-2.5 text-body text-foreground hover:bg-muted/50 focus:ring-[3px] focus:ring-primary/15 focus:border-primary outline-none transition-all disabled:opacity-50"
            aria-label={ariaLabel}
            disabled={disabled}
            data-testid={`${testId}-trigger`}
          >
            <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="tabular-nums font-normal">
              {chipRangeLabel(fromDay, toDay, placeholder)}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto max-w-[calc(100vw-16px)] p-0 flex flex-col shadow-md rounded-lg border bg-popover"
          align="start"
          collisionPadding={8}
          data-density="compact"
        >
          <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-border">
            {/* Presets list */}
            {presets && presets.length > 0 && (
              <div className="p-2 flex flex-row flex-wrap sm:flex-col gap-1 sm:gap-0.5 sm:min-w-[130px] shrink-0 bg-muted/20" data-testid={`${testId}-presets`}>
                <span className="hidden sm:block px-2 py-1 text-micro font-medium text-muted-foreground">Presets</span>
                {presets.map(p => {
                  const isActive = preset === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={cn(
                        "text-left px-2.5 py-1.5 rounded-md text-meta font-medium transition-colors whitespace-nowrap",
                        isActive
                          ? "bg-primary text-primary-foreground font-semibold"
                          : "text-foreground hover:bg-muted border sm:border-0"
                      )}
                      onClick={() => handleApplyPreset(p)}
                      data-testid={`${testId}-preset-${p.id}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Range Calendar */}
            <div className="p-2">
              <Calendar
                mode="range"
                selected={anchor ? { from: anchor, to: undefined } : selectedRange}
                defaultMonth={fromDay || maxDay || new Date()}
                onSelect={(_, day) => handleDayClick(day)}
                numberOfMonths={1}
                disabled={[minDay && { before: minDay }, maxDay && { after: maxDay }].filter(Boolean)}
                initialFocus
              />
            </div>
          </div>

          <div className="border-t px-3 py-1.5 text-micro text-muted-foreground" data-testid={`${testId}-hint`}>
            {anchor ? "Now pick the last day" : "Pick the first day"}
          </div>

          {/* Business-day footer (DESIGN.md §5.2) */}
          {businessDayStart && (
            <div className="border-t px-3 py-1.5 text-micro text-muted-foreground bg-muted/10 flex items-center justify-between" data-testid={`${testId}-business-day-footer`}>
              <span>Day starts {formatTimeHelper(businessDayStart)}</span>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default DateRangePicker;
